import { Flex, Text } from "@radix-ui/themes";
import { useState } from "react";

import { ArrowRightIcon, CheckIcon } from "@/assets/icons";
import NewReleaseChooser from "@/components/audiolab/NewReleaseChooser";
import CreateMenu from "@/components/nav/LeftNav/CreateMenu";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import { hasSellerPersona } from "@/lib/personas";
import { cn } from "@/lib/utils";

import { useGetStartedMilestones } from "./useGetStartedMilestones";

/**
 * "Get started" onboarding checklist pinned to the bottom of the Audiolab
 * sidebar. Artist-only, and it removes itself once every milestone is done —
 * each step reflects live account state (no dismiss flag to persist), so the
 * card simply disappears when the work is finished.
 */
export default function SidebarGetStartedTracker() {
  const { data: user } = useLoggedInUser();
  const isSeller = hasSellerPersona(user?.profile?.personas);
  const [chooserOpen, setChooserOpen] = useState(false);

  const { steps, completedCount, total, isLoading, allComplete } =
    useGetStartedMilestones({ enabled: isSeller });

  if (!isSeller || isLoading || allComplete) {
    return null;
  }

  // The first still-open step is highlighted, mirroring the mock — it points
  // the artist at the one thing to do next.
  const nextIncompleteIndex = steps.findIndex((step) => !step.complete);

  return (
    <Flex
      direction="column"
      className="mb-[16px] overflow-hidden rounded-[8px] border border-[#DCE1F9] bg-white"
      data-testid="sidebar-get-started-tracker"
    >
      <Flex
        direction="column"
        className="gap-[2px] bg-[#EEF1FE] px-[14px] py-[12px]"
      >
        <Text className="text-[15px] font-semibold text-text-primary">
          Get started
        </Text>
        <Text className="text-[13px] text-text-secondary">
          {completedCount} of {total} complete
        </Text>
      </Flex>

      <Flex direction="column">
        {steps.map((step, index) => {
          const isNext = index === nextIncompleteIndex;
          const rowClassName = cn(
            "flex w-full items-center gap-[8px] border-t border-[rgba(5,12,43,0.06)] px-[10px] py-[10px] text-left transition-colors hover:bg-background-quarternary",
            isNext && "bg-[#F5F7FF]",
          );
          const rowContent = (
            <>
              {step.complete ? (
                <div className="flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full bg-green-600">
                  <CheckIcon
                    width={12}
                    height={12}
                    className="fill-current text-white"
                  />
                </div>
              ) : (
                <div className="flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full bg-background-quarternary">
                  <span className="text-[11px] font-semibold text-text-secondary">
                    {index + 1}
                  </span>
                </div>
              )}

              <step.Icon
                width={16}
                height={16}
                className={cn(
                  "shrink-0 fill-current",
                  isNext ? "text-brand-primary" : "text-text-secondary",
                )}
              />

              <Text
                className={cn(
                  "flex-1 whitespace-nowrap text-[12px] font-semibold",
                  step.complete ? "text-text-secondary" : "text-text-primary",
                )}
              >
                {step.label}
              </Text>

              <ArrowRightIcon
                width={12}
                height={12}
                className="shrink-0 fill-current text-text-tertiary"
              />
            </>
          );

          // Publishing starts with a release, so the row opens the same
          // Single/Album chooser the Create menu uses.
          if (step.key === "publish") {
            return (
              <button
                key={step.key}
                type="button"
                onClick={() => setChooserOpen(true)}
                data-testid={`get-started-step-${step.key}`}
                className={rowClassName}
              >
                {rowContent}
              </button>
            );
          }

          if (step.key === "upload") {
            return (
              <CreateMenu
                key={step.key}
                side="right"
                trigger={
                  <button
                    type="button"
                    data-testid={`get-started-step-${step.key}`}
                    className={rowClassName}
                  >
                    {rowContent}
                  </button>
                }
              />
            );
          }

          return (
            <button
              key={step.key}
              type="button"
              onClick={step.onClick}
              data-testid={`get-started-step-${step.key}`}
              className={rowClassName}
            >
              {rowContent}
            </button>
          );
        })}
      </Flex>

      <NewReleaseChooser open={chooserOpen} onOpenChange={setChooserOpen} />
    </Flex>
  );
}
